import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { SHARE_EVENT_TO_EMAILS } from "../gql/queries/shareEvent";
import { Spacing } from "../types/enum";
import { extractEmails } from "./ChatAutomations/helpers";

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
}

/**
 * Share one event with a pasted / uploaded list of member emails — the same
 * "shared this event with you" heads-up as the Share button, from the event's
 * own Invite tab. A share is a notification, not an invite (no spot taken).
 */
const ShareByEmailDialog: React.FC<Props> = ({ open, onClose, eventId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState("");
  const [share, { loading }] = useMutation(SHARE_EVENT_TO_EMAILS);

  const emails = extractEmails(text);

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (file) {
      const content = await file.text();
      setText((t) => (t ? `${t}\n${content}` : content));
    }
    event.target.value = "";
  };

  const onShare = async () => {
    if (emails.length === 0) {
      enqueueSnackbar("Paste some emails first.", { variant: "info" });
      return;
    }
    try {
      const { data } = await share({
        variables: { eventId, emails, source: "SHARE_EVENT" },
      });
      const result = data?.adminShareEventToEmails;
      const shared = result?.sharedCount ?? 0;
      enqueueSnackbar(
        `Shared with ${shared} member${shared === 1 ? "" : "s"}.` +
          (result?.notFound?.length ? ` ${result.notFound.length} matched no member.` : ""),
        { variant: shared > 0 ? "success" : "warning" },
      );
      setText("");
      onClose();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Share failed.", {
        variant: "error",
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share this event by email</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary" display="block" mb={Spacing.s}>
          Paste or upload member emails — each gets a "shared this event with you" heads-up. A share
          is a notification, not an invite, so it never takes a spot.
        </Typography>
        <TextField
          label="Emails"
          placeholder="Paste emails — commas, spaces or new lines all work"
          value={text}
          onChange={(e) => setText(e.target.value)}
          helperText={emails.length > 0 ? `${emails.length} email(s) found` : " "}
          multiline
          minRows={5}
          fullWidth
        />
        <Box mt={Spacing.s}>
          <Button component="label" size="small" variant="text">
            📄 Upload .csv
            <input hidden type="file" accept=".csv,.txt" onChange={onFile} />
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onShare} disabled={loading || emails.length === 0}>
          {loading ? "Sharing…" : `Share with ${emails.length}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareByEmailDialog;
